import { useMemo, useSyncExternalStore } from 'react';

import type { ColorTheme, UserLanguage } from '~/api/user/types';
import type {
  RegistrationAccountInformation,
  RegistrationBusinessInformation,
  RegistrationReferralCodeInformation,
  RegistrationVatInformation,
} from '~/pages/Auth/Registration/Registration.context';

/** Note: local storage properties are always nullable because the user can clear their local storage during a session. */
type CraftLocalStorage = {
  colorTheme: ColorTheme | null;
  language: UserLanguage | null;
  /** LeadJourney click id, captured from the URL so a completed registration can be attributed to its campaign. */
  leadJourneyClickId: string | null;
  registrationAccountInformation: Omit<RegistrationAccountInformation, 'password'> | null;
  registrationBusinessInformation: RegistrationBusinessInformation | null;
  registrationReferralCodeInformation: RegistrationReferralCodeInformation | null;
  registrationVatInformation: RegistrationVatInformation | null;
  sidebar: 'expanded' | 'collapsed' | null;
};

const createKey = (key: string) => `craft-${key}`;

// Initialize local storage
Object.entries({
  sidebar: 'expanded',
  colorTheme: null,
  language: 'nl',
  leadJourneyClickId: null,
  registrationVatInformation: null,
  registrationAccountInformation: null,
  registrationBusinessInformation: null,
  registrationReferralCodeInformation: null,
} satisfies CraftLocalStorage).forEach(([key, value]) => {
  const item = window.localStorage.getItem(createKey(key));

  if (
    item === null ||
    // `sidebar` used to be stored without stringifying, which causes issues when trying to parse it.
    // 'expanded' should become '"expanded"' so it is parsable (same for 'collapsed').
    item === 'expanded' ||
    item === 'collapsed'
  )
    window.localStorage.setItem(createKey(key), JSON.stringify(value));
});

// Stored values can be corrupted (e.g. legacy unstringified values), so unparsable values fall back to `null`.
const parse = <K extends keyof CraftLocalStorage>(value: string | null) => {
  if (value === null) return null as CraftLocalStorage[K];

  try {
    return JSON.parse(value) as CraftLocalStorage[K];
  } catch {
    return null as CraftLocalStorage[K];
  }
};

// Curried getter and setter
const getItem =
  <K extends keyof CraftLocalStorage>(key: K) =>
  () => {
    return window.localStorage.getItem(createKey(key));
  };

const setItem =
  <K extends keyof CraftLocalStorage>(key: K) =>
  (newValue: CraftLocalStorage[K] | ((oldValue: CraftLocalStorage[K]) => CraftLocalStorage[K])) => {
    const stringifiedOldValue = window.localStorage.getItem(createKey(key));
    const parsedOldValue = parse<K>(stringifiedOldValue);

    const stringifiedNewValue = JSON.stringify(typeof newValue === 'function' ? newValue(parsedOldValue) : newValue);

    if (stringifiedOldValue === stringifiedNewValue) return;

    window.localStorage.setItem(createKey(key), stringifiedNewValue);

    // Manually dispatch storage event as it is normally only triggered on other tabs and windows.
    window.dispatchEvent(new StorageEvent('craft-storage', { key, newValue: stringifiedNewValue }));
  };

// Store subscription
const subscribe = (listener: () => void) => {
  window.addEventListener('craft-storage', listener);
  return () => window.removeEventListener('craft-storage', listener);
};

export const useCraftLocalStorage = <K extends keyof CraftLocalStorage>(key: K) => {
  const getSnapshot = useMemo(() => getItem(key), [key]);
  const value = useSyncExternalStore(subscribe, getSnapshot);
  const setValue = useMemo(() => setItem(key), [key]);

  const parsedValue = useMemo(() => parse<K>(value), [value]);
  return [parsedValue, setValue] as const;
};
